import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface StudioInfo {
  status: string;
  app_count: number;
  client_count: number;
  included_credits_usd: number;
  credits_used_usd: number;
  billing_period_start?: string | null;
  billing_period_end?: string | null;
}

interface UserData {
  user: {
    id: string;
    email: string;
    name: string;
    auth_provider: string;
    mfa_enabled: boolean;
    status: string;
    avatar_url: string | null;
    created_at: string;
  };
  profile: {
    display_name: string;
    bio: string | null;
    company: string | null;
    title: string | null;
    timezone: string | null;
    locale: string;
  } | null;
  tenants: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
  }>;
  current_tenant_id: string | null;
  studio: StudioInfo | null;
}

interface AuthContextValue {
  user: UserData | null;
  studio: StudioInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isStudioSubscribed: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const data = await api<UserData>('auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    await api('auth/login', { body: { email, password } });
    await refresh();
  }, [refresh]);

  const register = useCallback(async (email: string, password: string, name: string) => {
    await api('auth/register', { body: { email, password, name } });
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await api('auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    queryClient.clear();
    // Redirect to Hub login after clearing local session
    const hubUrl = import.meta.env.VITE_HUB_URL || 'https://my.theonestack.com';
    window.location.href = `${hubUrl}/login`;
  }, [queryClient]);

  const switchTenant = useCallback(async (tenantId: string) => {
    await api('auth/switch-tenant', { body: { tenant_id: tenantId } });
    await refresh();
    queryClient.clear();
  }, [refresh, queryClient]);

  const studio = user?.studio ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        studio,
        isLoading,
        isAuthenticated: !!user,
        isStudioSubscribed: studio?.status === 'active',
        login,
        register,
        logout,
        switchTenant,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
